import { useEffect, useMemo } from 'react';
import { getIsResourceAvailableToUse } from '@lp/features/RoutePlanner/utils';
import { useBaseCaseDetails } from '@/hooks/useBaseCaseDetails';
import { useSurgeonsForAssignment } from '@/queries/resource';
import { CandidateMatchType, Employee } from '@/api/apis/resources';
import { SuggestedEmployeeInfo } from './SuggestedEmployeeInfo';
import { useSuggestedRouteValidationContext } from './SuggestedRouteValidationContext';

type SuggestedResourceRSProps = {
  suggestedRouteId: string;
  resourceId: string;
  fallbackInfo: Pick<Employee, 'id' | 'firstName' | 'lastName' | 'profilePhoto'> | null;
  orTimeUTC: string;
};

export const SuggestedResourceRS = ({
  suggestedRouteId,
  resourceId,
  fallbackInfo,
  orTimeUTC,
}: SuggestedResourceRSProps) => {
  const { setRSResource } = useSuggestedRouteValidationContext();
  const { caseId, organType, donorType, caseName } = useBaseCaseDetails();
  const { data: surgeons, isLoading } = useSurgeonsForAssignment({
    caseId,
    donor: donorType,
    organ: organType,
    orTime: orTimeUTC,
  });

  const currentSurgeon = useMemo(() => {
    if (!surgeons) return null;

    return surgeons.find((rs) => rs.employeeId === resourceId) ?? null;
  }, [resourceId, surgeons]);

  useEffect(() => {
    const isAvailable = getIsResourceAvailableToUse(currentSurgeon);
    setRSResource(suggestedRouteId, isAvailable);
  }, [suggestedRouteId, currentSurgeon, setRSResource]);

  const isWarningState = !currentSurgeon || currentSurgeon.match.matchType !== CandidateMatchType.Perfect;

  return (
    <SuggestedEmployeeInfo
      isLoading={isLoading}
      isWarningState={isWarningState}
      employee={currentSurgeon}
      fallbackInfo={fallbackInfo}
      type="retrieval-surgeon"
      orTimeUTC={orTimeUTC}
      organType={organType}
      caseName={caseName}
    />
  );
};
